/// <amd-module name="Wit/Common/WitSettingsPanel/Components/WitSettingsPanel" />
import * as React from "react";
import { ObservableValue } from "VSS/Core/Observable";
import { ScreenSize } from "VSS/Core/Util/Screen";
import { format } from "VSS/Core/Util/String";
import { Components, IVssLayoutManager, usePageContext } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { ButtonGroup } from "VSSUI/ButtonGroup";
import { ContentSize } from "VSSUI/Callout";
import { Dialog } from "VSSUI/Dialog";
import { IListItemDetails, List, ListItem, ListSelection } from "VSSUI/List";
import { IListBoxGroup, IListBoxItem, ListBoxItemType } from "VSSUI/ListBox";
import { SingleLayerMasterPanel } from "VSSUI/MasterDetails";
import { bindSelectionToObservable } from "VSSUI/MasterDetailsContext";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { CustomPanel, PanelContent, PanelFooter, PanelHeader, PanelOverlay } from "VSSUI/Panel";
import { Spinner } from "VSSUI/Spinner";
import { Status, Statuses, StatusSize } from "VSSUI/Status";
import { css } from "VSSUI/Util";
import { GroupedItemProvider } from "VSSUI/Utilities/GroupedItemProvider";
import { IItemProvider } from "VSSUI/Utilities/Provider";
import { ScreenSizeObserver } from "VSSUI/Utilities/ScreenSize";
import { ISelectionRange } from "VSSUI/Utilities/Selection";
import * as Resources from "Wit/Common/WitSettingsPanel/Resources";
import { ISettingsView, IWitSettingsPanelProps } from "Wit/Common/WitSettingsPanelTypes/Components/WitSettingsPanel.props";

/**
 * WIT settings panel that manages displayed content, dirtiness, and validity of settings data.
 * Can be configured to show any settings view.
 */
export const WitSettingsPanel: React.FunctionComponent<IWitSettingsPanelProps> = ({
    defaultActiveElement,
    settingsGroups,
    onDismiss,
    onSave,
    onBeforeSettingsSaved,
    savingSpinnerLabel,
    settingsViews,
    titleProps
}) => {
    const pageContext = usePageContext();
    const errorMessage = new ObservableValue("");
    const isValid = new ObservableValue(true);
    const isDirty = new ObservableValue(false);
    const showOverlay = new ObservableValue(false);
    const itemProvider = getGroupedItemProvider(settingsViews, settingsGroups);
    const selection = new ListSelection({
        selectOnFocus: false,
        unselectableRanges: itemProvider.value.reduce((aggregate: ISelectionRange[], item: IListBoxItem<ISettingsView>, index: number) => {
            item.type === ListBoxItemType.Header && aggregate.push({ beginIndex: index, endIndex: index });
            return aggregate;
        }, [])
    });
    const selectedItem = new ObservableValue(itemProvider.value[0]);
    bindSelectionToObservable(selection, itemProvider, selectedItem);

    const renderMasterContent = (selection: ListSelection, itemProvider: IItemProvider<IListBoxItem<ISettingsView>>): JSX.Element => {
        return <List itemProvider={itemProvider} selection={selection} renderRow={renderListItem} width="100%" singleClickActivation={true} />;
    };

    const renderListItem = (
        index: number,
        item: IListBoxItem<ISettingsView>,
        details: IListItemDetails<IListBoxItem<ISettingsView>>,
        key?: string
    ): JSX.Element | null => {
        if (item.type === ListBoxItemType.Divider) {
            return null;
        }

        const settingsItem = item.data;
        let innerContent: JSX.Element | null = null;
        const isHeader = item.type === ListBoxItemType.Header;
        if (isHeader) {
            innerContent = <div className="primary-text title-s text-ellipsis flex-grow font-weight-semibold">{item.text}</div>;
        } else if (settingsItem) {
            innerContent = (
                <>
                    <Observer isDirty={settingsItem.isDirty}>
                        {(props: { isDirty: boolean }) => (
                            <div className={css("primary-text text-ellipsis flex-grow", props.isDirty && "font-weight-semibold")}>
                                {props.isDirty ? format(Resources.DirtyLabelSuffix, settingsItem.name) : settingsItem.name}
                            </div>
                        )}
                    </Observer>
                    <Observer isValid={settingsItem.isValid}>
                        {(props: { isValid: boolean }) => (!props.isValid ? <Status {...Statuses.Failed} key="error" size={StatusSize.m} /> : null)}
                    </Observer>
                </>
            );
        }

        return (
            <ListItem className={css(isHeader && "bolt-list-row-header")} key={key || "list-item" + index} index={index} details={details}>
                <div
                    className={css(
                        "flex-row flex-grow flex-center h-scroll-hidden padding-vertical-8 padding-right-8 padding-left-16",
                        isHeader && "margin-top-16"
                    )}
                >
                    {innerContent}
                </div>
            </ListItem>
        );
    };

    const onSaveClick = async () => {
        try {
            await onBeforeSettingsSaved?.();
            
            showOverlay.value = true;
            const saveSettingsPromises = settingsViews
                .filter(view => view.isDirty.value && !!view.onSaveSettings)
                .map(view => view.onSaveSettings?.());
            await Promise.all(saveSettingsPromises);
            const closePanel = await onSave();
            closePanel && onDismiss();
        } catch (ex) {
            errorMessage.value = ex.message;
            showOverlay.value = false;
        }
    };

    const wrappedOnDismiss = () => {
        if (isDirty.value) {
            const layoutService = pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
            layoutService.renderCallout(onCalloutDismiss => {
                const onDismissSettings = () => {
                    onCalloutDismiss();
                    onDismiss();
                };

                const onSaveSettings = () => {
                    onCalloutDismiss();
                    onSaveClick();
                };

                return (
                    <ConfirmCloseDialog onDimissSettings={onDismissSettings} onKeepSettingsOpen={onCalloutDismiss} onSaveSettings={onSaveSettings} />
                );
            });
        } else {
            onDismiss();
        }
    };

    const onViewDirtyChange = (value: boolean, action: string) => {
        // iterate through settings views to determine if the overall view is dirty or not
        let dirty = false;
        for (const view of settingsViews) {
            if (view.isDirty.value) {
                dirty = true;
                break;
            }
        }

        // update overall dirtiness of settings
        if (dirty !== isDirty.value) {
            isDirty.value = dirty;
        }
    };

    const onViewValidityChange = () => {
        // iterate through settings views to determine if the overall view is dirty or not
        let valid = true;
        for (const view of settingsViews) {
            if (!view.isValid.value) {
                valid = false;
                break;
            }
        }

        // update overall dirtiness of settings
        if (valid !== isValid.value) {
            isValid.value = valid;
        }
    };

    // subscribe master isDirty observable to update whenever a view dirty observable changes
    React.useEffect(() => {
        settingsViews.forEach(view => view.isDirty.subscribe(onViewDirtyChange));

        // clean up when component is unmounting
        return () => {
            settingsViews.forEach(view => view.isDirty.unsubscribe(onViewDirtyChange));
        };
    });

    // subscribe master isValid observable to update whenever a view isValid observable changes
    React.useEffect(() => {
        settingsViews.forEach(view => view.isValid.subscribe(onViewValidityChange));

        // clean up when component is unmounting
        return () => {
            settingsViews.forEach(view => view.isValid.unsubscribe(onViewValidityChange));
        };
    });

    return (
        <ScreenSizeObserver>
            {({ screenSize }: { screenSize: ScreenSize }) => (
                <CustomPanel
                    className="wit-settings-panel"
                    defaultActiveElement={defaultActiveElement}
                    onDismiss={wrappedOnDismiss}
                    size={screenSize <= Number(ScreenSize.small) ? ContentSize.Large : ContentSize.Auto}
                >
                    <PanelHeader onDismiss={onDismiss} titleProps={titleProps} />
                    <PanelContent>
                        <SingleLayerMasterPanel
                            className="wit-settings-master-panel"
                            renderContent={() => renderMasterContent(selection, itemProvider)}
                            showOnSmallScreens={true}
                        />
                        <div className="wit-settings-content flex-column flex-grow">
                            <Observer errorMessage={errorMessage}>
                                {(props: { errorMessage: string | undefined }) =>
                                    props.errorMessage ? (
                                        <MessageCard className="flex-noshrink margin-bottom-16" severity={MessageCardSeverity.Error}>
                                            {props.errorMessage}
                                        </MessageCard>
                                    ) : null
                                }
                            </Observer>
                            <Observer selectedItem={selectedItem}>
                                {(props: { selectedItem: IListBoxItem<ISettingsView> }) => props.selectedItem.data?.renderContent()}
                            </Observer>
                        </div>
                    </PanelContent>
                    <PanelFooter>
                        <ButtonGroup className="bolt-panel-footer-buttons">
                            <Button text={Resources.CancelButtonText} onClick={onDismiss} />
                            <Observer isDirty={isDirty} isValid={isValid}>
                                {(props: { isDirty: boolean; isValid: boolean }) => (
                                    <Button
                                        disabled={!props.isDirty || !props.isValid}
                                        onClick={onSaveClick}
                                        primary
                                        text={Resources.SaveButtonText}
                                    />
                                )}
                            </Observer>
                        </ButtonGroup>
                    </PanelFooter>
                    <Observer showOverlay={showOverlay}>
                        {(props: { showOverlay: boolean }) =>
                            props.showOverlay ? <PanelOverlay overlayContent={<Spinner label={savingSpinnerLabel} />} /> : null
                        }
                    </Observer>
                </CustomPanel>
            )}
        </ScreenSizeObserver>
    );
};

Components.add("ms.vss-work-web.wit-settings-panel", WitSettingsPanel);

interface IConfirmCloseDialog {
    onDimissSettings: () => void;
    onKeepSettingsOpen: () => void;
    onSaveSettings: () => void;
}

const ConfirmCloseDialog: React.FunctionComponent<IConfirmCloseDialog> = ({ onDimissSettings, onKeepSettingsOpen, onSaveSettings }) => (
    <Dialog
        titleProps={{ text: Resources.UnsavedChanges }}
        onDismiss={onDimissSettings}
        lightDismiss={false}
        footerButtonProps={[
            { text: Resources.CancelButtonText, onClick: onKeepSettingsOpen },
            { text: Resources.DiscardChangesButtonText, onClick: onDimissSettings },
            {
                text: Resources.SaveChangesButtonText,
                onClick: onSaveSettings,
                primary: true
            }
        ]}
    >
        <div>{Resources.UnsavedChangesMessage}</div>
    </Dialog>
);

function getGroupedItemProvider(views: ISettingsView[], groups?: IListBoxGroup<ISettingsView>[]): GroupedItemProvider<ISettingsView> {
    const viewListBoxItems: IListBoxItem<ISettingsView>[] = views.map(view => {
        return {
            data: view,
            groupId: view.groupId,
            id: view.name
        };
    });

    return new GroupedItemProvider(viewListBoxItems, groups ?? [], true);
}
